import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import $api from '../api'

export interface User {
  email: string
  password: string
  name: string
  phone: string
  identifier: string
  validatedAt?: number
  bannedAt?: number
  registeredAt: number
}

interface State {
  token?: string
  tokenExpiresAt?: number
  refreshToken?: string
  refreshTokenExpiresAt?: number
  user?: User
}

export interface AuthDTO {
  email: string
  password: string
}

export interface RegisterDTO extends AuthDTO {
  phone: string
  name: string
  city: string
  businessType: string
  identifier: string
}

export interface AuthResponseDTO {
  token: string
  refreshToken?: string
  user?: User
}

interface RegisterResponseDTO {
  auth: AuthResponseDTO
  user: User
}

export type ClaimResetPasswordCodeDTO = Pick<AuthDTO, 'email'> & { code: number }

export const attemptLogin = createAsyncThunk<AuthResponseDTO, AuthDTO>(
  'auth/authorization',
  async (payload) => {
    const response = await $api.post('user/auth', payload)
    return response.data
  },
)

export const attemptRegister = createAsyncThunk<RegisterResponseDTO, RegisterDTO>(
  'auth/registration',
  async (payload) => {
    const response = await $api.post('user/registration', payload)
    return response.data
  },
)

export const attemptSendResetPasswordCode = createAsyncThunk<string, Pick<AuthDTO, 'email'>>(
  'auth/forgot-password/send-code',
  async (payload) => {
    const response = await $api.post('user/forgot-password/send-code', payload)
    return payload.email
  },
)

export const attemptClaimResetPasswordCode = createAsyncThunk<
  AuthResponseDTO,
  ClaimResetPasswordCodeDTO
>('auth/forgot-password/claim-code', async (payload) => {
  const response = await $api.post('user/forgot-password/claim-code', payload)
  return response.data
})

export const initialState: State = {}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: () => {
      // Handled in store.ts in rootReducer
    },
  },
  extraReducers: (builder) => {
    builder.addCase(attemptLogin.fulfilled, (state, { payload }) => {
      state.token = payload.token
      state.user = payload.user
    })
    builder.addCase(attemptRegister.fulfilled, (state, { payload }) => {
      state.token = payload.auth.token
      state.refreshToken = payload.auth.refreshToken
      state.user = payload.user
    })
    builder.addCase(attemptClaimResetPasswordCode.fulfilled, (state, { payload }) => {
      state.token = payload.token
      state.user = payload.user
    })
  },
})

export const { logout } = authSlice.actions
export default authSlice.reducer
